import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Info } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { isDemoMode, fmtPrice } from "@/components/DemoHelpers";
import { useDemoAction } from "@/components/DemoActionDialog";
import {
  purchaseReducer,
  initialPurchaseState,
} from "./purchaseReducer";
import PropertyStage from "./stages/PropertyStage";
import PropertyDocsStage from "./stages/PropertyDocsStage";
import { attachAnalyzedDocs } from "./stages/AiPropertyCreate";
import ChainedPrereqStage from "./stages/ChainedPrereqStage";
import UnifiedRequirementsStage from "./UnifiedRequirementsStage";
import QuoteStage from "./stages/QuoteStage";
import PurchaseStage from "./stages/PurchaseStage";
import ShareLinkStage from "./stages/ShareLinkStage";
import { FlexiblePriceWizard, computeFlexibleSupplements } from "@/components/FlexiblePriceWizard";

const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

// Iter209dzw — Prezzo unitario PRO: il backend applica lo sconto fascia su
// (base + delta flessibile) e arrotonda per eccesso al multiplo di 5.
export function computeUnitPrice(service, flexDelta) {
  const d = Number(service?.fascia_discount_pct) || 0;
  if (d > 0 && flexDelta && !service?.pro_markup_applied_pct) {
    const raw = (Number(service.base_price) || 0) + flexDelta;
    return Math.ceil(Math.round(raw * (1 - d / 100) * 100) / 100 / 5) * 5;
  }
  const unitBase = Number(service?.professional_price || service?.base_price || 0);
  return Math.round((unitBase + flexDelta) * 100) / 100;
}

// Iter209e7 — Icona "i" cerchiata con Popover, usabile dentro card cliccabili
// (blocca la propagazione del click così la card non viene selezionata).
function CardInfoPopover({ children, testId }) {
  const stop = (e) => e.stopPropagation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          aria-label="Dettagli"
          onClick={stop}
          onKeyDown={stop}
          className="inline-flex items-center text-purple-500 hover:text-purple-800 cursor-pointer"
          data-testid={testId}
        >
          <Info size={18} weight="regular" />
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 text-xs text-slate-700 leading-relaxed z-[200]"
        onClick={stop}
        data-testid={`${testId}-content`}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

// Iter117: helper "pure" per calcolare il delta EUR del Prezzo Flessibile
// data la sequenza di risposte scelte dall'utente. Rispecchia la logica
// backend (_apply_pricing_logic) per garantire prezzo consistente in UI.
function computeFlexibleDelta(service, answers) {
  const sum = computeFlexibleSupplements(service, answers).reduce((s, x) => s + x.amount, 0);
  return Math.round(sum * 100) / 100;
}

// Variazione € per scelta sopralluogo (rispecchia backend skip_visit_price_delta).
export function computeVisitDelta(service, proIds, skipped) {
  if (!service?.can_skip_visit || !service?.skip_visit_pricing_enabled) return 0;
  const allowed = service.skip_visit_pricing_professionals || [];
  if (service.skip_visit_pricing_scope === "selected" && ![].concat(proIds).some((id) => id && allowed.includes(id))) return 0;
  return Number(skipped ? service.skip_visit_price_skip : service.skip_visit_price_no_skip) || 0;
}

// Iter117: verifica se il flusso di domande è stato completato dall'utente.
// Iter135: verifica se il questionario del Prezzo Flessibile è COMPLETO.
// Cammina l'albero (radici → figli via `parent_answer_id`) esattamente come
// il wizard e come `_apply_pricing_logic` backend. Il flusso è completo
// quando OGNI domanda visibile ha ricevuto una risposta.
function isFlexiblePriceComplete(service, answers) {
  if (!service?.has_flexible_price) return true;
  const logic = Array.isArray(service.pricing_logic) ? service.pricing_logic : [];
  if (logic.length === 0) return true;
  const sid = (v) => (v === null || v === undefined ? "" : String(v));
  // Iter209h — primary answer id: single-select usa answer_id, multi-select
  // usa la selezione con posizione più bassa in question.answers[].
  const primaryAnswerId = (answered, question) => {
    if (!answered) return "";
    if (answered.answer_id) return sid(answered.answer_id);
    const ids = Array.isArray(answered.answer_ids) ? answered.answer_ids.map(sid) : [];
    if (ids.length === 0) return "";
    for (const a of (question?.answers || [])) {
      if (ids.includes(sid(a.id))) return sid(a.id);
    }
    return ids[0];
  };
  const answersMap = new Map(
    (answers || [])
      .filter(a => a && a.question_id)
      .map(a => [sid(a.question_id), a]),
  );
  const sorted = [...logic].filter(q => q && q.id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const roots = sorted.filter(q => q.is_root || !q.parent_question_id || !q.parent_answer_id);
  if (roots.length === 0) return true;
  const visited = new Set();
  const isMissing = (question) => {
    if (!question) return false;
    const qid = sid(question.id);
    if (visited.has(qid)) return false;
    visited.add(qid);
    const answered = answersMap.get(qid);
    if (!answered) return true;
    const primary = primaryAnswerId(answered, question);
    const children = sorted.filter(
      c => sid(c.parent_question_id) === qid && sid(c.parent_answer_id) === primary,
    );
    for (const c of children) {
      if (isMissing(c)) return true;
    }
    return false;
  };
  for (const root of roots) {
    if (isMissing(root)) return false;
  }
  return true;
}
const demo = () => isDemoMode();

/**
 * Modal di acquisto/preventivo del Marketplace.
 * Gestisce internamente ~15 pezzi di stato tramite un unico useReducer.
 * Gli stage sono in file dedicati sotto ./stages/.
 *
 * Props:
 *  - selected: servizio selezionato (aprire dialog) o null
 *  - services: catalogo completo (per lookup dei chained services al re-open)
 *  - properties: immobili dell'utente
 *  - user: utente corrente (per isSubUser e margini)
 *  - openIntent: { form?, stage? } — quando presente, seeda lo stato modale al re-open
 *  - onClose: () => void — chiude e resetta selected nel parent
 *  - onPropertiesChanged: () => Promise<void> — refresh lista immobili dopo creazione
 *  - onQuotesChanged: () => void — refresh preventivi dopo generazione
 *  - onPurchaseSuccess: () => void — callback dopo la chiusura del modal di successo
 *      (usato dal parent per fare loadInProgress+loadRecap e switchare alla tab)
 */
export default function PurchaseModal({
  selected,
  services,
  properties,
  user,
  plafond = null,
  openIntent = null,
  onBehalfOfProId = null,
  onClose,
  onPropertiesChanged,
  onQuotesChanged,
  onPurchaseSuccess,
  extraPayload = null,
}) {
  const [state, dispatch] = useReducer(purchaseReducer, initialPurchaseState);
  const promptIfDemo = useDemoAction();
  // Diritti ente policy: caricata al primo render, mostrata in modal quando
  // il servizio corrente ha `has_entity_fees=true`.
  const [entityFeesPolicyHtml, setEntityFeesPolicyHtml] = useState("");
  const [entityFeesGateOpen, setEntityFeesGateOpen] = useState(false);
  const [entityFeesAcked, setEntityFeesAcked] = useState(false);
  // Iter65: salto sopralluogo (Pro sceglie di rinunciare al sopralluogo).
  // `visitSkipped` è la scelta corrente (impostata dallo stage qty_visit).
  const [visitSkipped, setVisitSkipped] = useState(false);
  // Iter209er — Scelta esplicita nello stage "qty_visit": null | "yes" | "no".
  const [visitDecision, setVisitDecision] = useState(null);
  // Iter66: catalogo documenti + checklist per la pratica in creazione
  const [documentCatalog, setDocumentCatalog] = useState([]);
  const [documentChecklist, setDocumentChecklist] = useState({});
  const [documentsDisclaimerAcked, setDocumentsDisclaimerAcked] = useState(false);
  const [docsAckOpen, setDocsAckOpen] = useState(false);
  // Iter117: Prezzo Flessibile — risposte selezionate dal PRO durante il purchase.
  // Struttura: [{question_id, question_label, answer_id, answer_label,
  //              price_modifier_type, price_modifier_value, delta_applied_eur}]
  const [pricingAnswers, setPricingAnswers] = useState([]);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  // Iter209e2 — Blocca doppio invio dell'acquisto (state per la UI, ref per
  // il guard sincrono contro doppio click prima del re-render).
  const [isPurchasing, setIsPurchasing] = useState(false);
  const purchasingRef = useRef(false);
  // Iter127: reset delle risposte del Prezzo Flessibile. Va chiamato:
  //  1) alla chiusura del modal (X / click fuori),
  //  2) su ogni tasto "Indietro" nel flusso di acquisto,
  //  3) all'apertura di un nuovo servizio (init).
  const resetPricing = useCallback(() => setPricingAnswers([]), []);
  const visitProIds = [onBehalfOfProId || user?._id, user?.parent_user_id];
  const visitDelta = computeVisitDelta(selected, visitProIds, visitSkipped);

  // Iter132: prezzo professional aggiornato con le maggiorazioni/sconti del
  // Prezzo Flessibile. Usato per il riepilogo "Totale pratica" sopra i tasti
  // di pagamento e per la stage di purchase.
  // Iter152 + Iter154: `adjustedProfessionalPrice` replica ESATTAMENTE la
  // logica del riquadro "ANTEPRIMA COSTI" (linee 754-787). Include:
  //   1. Prezzo base + delta Prezzo Flessibile (per unità).
  //   2. Sconto tier da `quantity_discount_tiers` (matching sulla quantità).
  //   3. Moltiplicazione per `form.quantity` (se il servizio ammette qty > 1).
  //   4. Somma dei servizi accessori chained scelti come "purchase".
  // Prima il calcolo si fermava a unit+flex e ignorava tier discount e
  // moltiplicatore qty → recap "Imponibile" / "Totale cliente" incoerenti
  // rispetto alla preview costi mostrata poco sopra (differenza di prezzo
  // visibile all'utente).
  const adjustedProfessionalPrice = useMemo(() => {
    if (!selected) return 0;
    const flexDelta = computeFlexibleDelta(selected, pricingAnswers);
    const unit = computeUnitPrice(selected, flexDelta + visitDelta);
    // Quantità effettiva (bounded [1..10] come nella preview).
    const qty = selected.allow_multiple_quantity
      ? Math.max(1, Math.min(10, Number(state?.form?.quantity) || 1))
      : 1;
    // Sconto tier: seleziona la % più alta tra i tier soddisfatti (min_qty <= qty),
    // clampata a [0..50]. Coerente con backend & CostPreview.
    let discountPct = 0;
    if (qty > 1 && Array.isArray(selected.quantity_discount_tiers)) {
      for (const t of selected.quantity_discount_tiers) {
        const m = Number(t?.min_qty) || 0;
        const p = Number(t?.discount_pct) || 0;
        if (m > 0 && qty >= m && p > discountPct) discountPct = p;
      }
      discountPct = Math.min(50, Math.max(0, discountPct));
    }
    const discountedUnit = discountPct > 0
      ? Math.ceil(Math.round(unit * (1 - discountPct / 100) * 100) / 100 / 5) * 5
      : unit;
    const primaryTotal = Math.round(discountedUnit * qty * 100) / 100;
    // Accessori chained scelti come "purchase".
    const chainedTotal = (state?.chainedServicesDetails || []).reduce((sum, s) => {
      if (state?.chainedChoices?.[s._id] !== "purchase") return sum;
      return sum + Number(s.professional_price || s.base_price || 0);
    }, 0);
    return Math.round((primaryTotal + chainedTotal) * 100) / 100;
  }, [selected, pricingAnswers, visitDelta, state?.form?.quantity, state?.chainedServicesDetails, state?.chainedChoices]);
  // Iter119: metodi di pagamento attivi (fetch al mount).
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [housingFeeRate, setHousingFeeRate] = useState(0.3);
  const [appSettings, setAppSettings] = useState({ enable_share_with_client: true });
  useEffect(() => {
    api.get("/app-settings").then(r => setAppSettings(r.data || {})).catch(() => {});
  }, []);
  const directMethods = paymentMethods.filter((m) => m.enabled && m.visible_to_pro !== false);
  useEffect(() => {
    api.get("/payment-methods")
      .then(r => setPaymentMethods(r.data || []))
      .catch(() => setPaymentMethods([]));
    api.get("/payment-settings")
      .then(r => { const v = Number(r.data?.housing_fee_rate); if (Number.isFinite(v)) setHousingFeeRate(v); })
      .catch(() => {});
  }, []);
  const isMethodEnabled = (type) => {
    const m = paymentMethods.find(x => x.type === type);
    // Se il fetch non ha ancora popolato la lista o l'endpoint fallisce,
    // consideriamo il metodo abilitato per non bloccare l'utente.
    if (!m) return paymentMethods.length === 0;
    return !!m.enabled;
  };
  const getMethod = (type) => paymentMethods.find(x => x.type === type);
  useEffect(() => {
    api.get("/document-catalog")
      .then(r => setDocumentCatalog(r.data || []))
      .catch(() => setDocumentCatalog([]));
  }, []);
  // Reset checklist quando cambia il servizio selezionato
  useEffect(() => {
    setDocumentChecklist({});
    setDocumentsDisclaimerAcked(false);
  }, [selected?._id]);
  useEffect(() => {
    api.get("/public/entity-fees-policy")
      .then(r => setEntityFeesPolicyHtml(r.data?.content_html || ""))
      .catch(() => setEntityFeesPolicyHtml(""));
  }, []);
  // Reset ack quando cambia il servizio selezionato
  useEffect(() => {
    setEntityFeesAcked(false);
    setVisitSkipped(false);
    setVisitDecision(null);
    // Iter127: reset del Prezzo Flessibile all'apertura di un nuovo servizio
    // (equivalente a `initForService`) — la personalizzazione riparte da zero.
    resetPricing();
  }, [selected?._id, resetPricing]);
  // Preventivo convertito in ordine: ripristina la scelta sopralluogo del preventivo.
  useEffect(() => {
    if (openIntent?.visitSkipped) {
      setVisitSkipped(true);
      setVisitDecision("no");
    }
  }, [selected?._id, openIntent]);
  const {
    stage,
    form,
    quoteForm,
    generatingQuote,
    slotMonth,
    availability,
    chosenSlot,
    selectedDate,
    visitOnFirstStep,
    chainedChoices,
    chainedServicesDetails,
    subMarginPct,
    newPropForm,
    creatingProp,
    createMode,
    propSearch,
    shareLinkInfo,
    successInfo,
    intent,
    property_docs_files,
  } = state;

  const isSubUser = !!user?.parent_user_id;

  // Init reducer alla apertura del modal (selected: null → svc)
  useEffect(() => {
    if (!selected) return;
    const chained = (selected.chained_service_ids || [])
      .map((cid) => services.find((x) => x._id === cid))
      .filter(Boolean);
    dispatch({
      type: "INIT_FOR_SERVICE",
      service: selected,
      chained,
      overrides: openIntent || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?._id]);

  // Carica info workflow (visitOnFirstStep) + availability quando serve
  useEffect(() => {
    if (!selected) return;
    api
      .get(`/services/${selected._id}/workflow`)
      .then((r) => {
        const steps = (r.data?.steps || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        const firstIsVisit = steps.length > 0 && !!steps[0].is_visit;
        dispatch({ type: "SET_FIELD", field: "visitOnFirstStep", value: firstIsVisit });
      })
      .catch(() =>
        dispatch({ type: "SET_FIELD", field: "visitOnFirstStep", value: false }),
      );

    if (!selected.requires_visit) {
      dispatch({ type: "SET_FIELD", field: "availability", value: null });
      return;
    }
    api
      .get("/calendar/combined-availability", {
        params: { service_id: selected._id, year: slotMonth.year, month: slotMonth.month },
      })
      .then((r) => dispatch({ type: "SET_FIELD", field: "availability", value: r.data }))
      .catch(() =>
        dispatch({ type: "SET_FIELD", field: "availability", value: { slots: [] } }),
      );
  }, [selected, slotMonth]);

  // Raggruppa slot per data
  const slotsByDate = useMemo(() => {
    const m = {};
    for (const s of availability?.slots || []) {
      if (!m[s.date]) m[s.date] = [];
      m[s.date].push(s);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => (a.hour - b.hour) || ((a.minute || 0) - (b.minute || 0)));
    return m;
  }, [availability]);

  const monthGrid = useMemo(() => {
    const y = slotMonth.year;
    const m = slotMonth.month;
    const first = new Date(y, m - 1, 1);
    const firstDayIdx = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDayIdx; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, date: dateStr, hasSlots: (slotsByDate[dateStr] || []).length > 0 });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [slotMonth, slotsByDate]);

  const slotsForSelectedDate = selectedDate ? slotsByDate[selectedDate] || [] : [];
  const totalSlotsCount = (availability?.slots || []).length;

  // Handlers
  const submitNewProperty = async (override, attachFiles, opts = {}) => {
    // Iter209es — `override`: valori confermati dal flusso "Creazione con AI".
    const npf = override && typeof override === "object" && !override.preventDefault
      ? { ...newPropForm, ...override } : newPropForm;
    const isAzienda = npf.owner_type === "azienda";
    const isPrivato = npf.owner_type === "privato";
    if (!isAzienda && !isPrivato) {
      toast.error("Seleziona Privato o P.IVA prima di continuare");
      return;
    }
    if (!npf.address.trim() || !npf.city.trim()) {
      toast.error("Compila indirizzo e città dell'immobile");
      return;
    }
    // Cliente scelto dalla rubrica: i dati sono bloccati, non si rivalidano.
    if (opts.fromExistingClient) {
      // nessun controllo sui campi proprietario
    } else if (isAzienda) {
      // Iter209dp — Solo Ragione sociale + P.IVA sono obbligatorie.
      // Indirizzo sede legale, Codice Fiscale azienda, Telefono, Email
      // restano visibili ma opzionali.
      if (
        !npf.owner_company_name.trim() ||
        !npf.owner_vat_number.trim()
      ) {
        toast.error("Compila Ragione sociale e P.IVA");
        return;
      }
    } else {
      if (
        !npf.owner_first_name.trim() ||
        !npf.owner_last_name.trim()
      ) {
        toast.error("Compila nome e cognome del proprietario");
        return;
      }
    }
    const doIt = async () => {
      dispatch({ type: "SET_FIELD", field: "creatingProp", value: true });
      try {
        const r = await api.post("/properties", {
          ...npf,
          on_behalf_of_professional_id: onBehalfOfProId || undefined,
        });
        const p = r.data;
        if (attachFiles?.length) await attachAnalyzedDocs(p._id || p.id, attachFiles);
        await onPropertiesChanged?.();
        dispatch({ type: "PATCH_FORM", patch: { property_id: p._id || p.id } });
        dispatch({ type: "SET_FIELD", field: "createMode", value: false });
        dispatch({ type: "RESET_NEW_PROP_FORM" });
        toast.success("Immobile creato e selezionato");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Errore creazione immobile");
      } finally {
        dispatch({ type: "SET_FIELD", field: "creatingProp", value: false });
      }
    };
    promptIfDemo(doIt, { label: "Creazione nuovo immobile" });
  };

  const purchase = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isPurchasing || purchasingRef.current) return;
    if (visitOnFirstStep && !form.is_shared_with_client && !chosenSlot && !visitSkipped) {
      toast.error(
        selected?.can_skip_visit
          ? "Seleziona uno slot per il sopralluogo oppure torna indietro e scegli di saltarlo"
          : "Seleziona uno slot per il sopralluogo",
      );
      return;
    }
    // Iter117: se il servizio ha Prezzo Flessibile, l'utente deve completare il wizard
    // (l'ultima domanda visibile deve essere risposta) prima di procedere.
    if (selected?.has_flexible_price && Array.isArray(selected?.pricing_logic) && selected.pricing_logic.length > 0) {
      if (!isFlexiblePriceComplete(selected, pricingAnswers)) {
        toast.error("Rispondi a tutte le domande di personalizzazione per calcolare il prezzo");
        return;
      }
    }
    // Gate diritti ente: se il servizio prevede pagamento diritti ente e l'utente
    // non ha ancora confermato l'informativa, apri il modal informativo.
    if (selected?.has_entity_fees && !entityFeesAcked && !opts.skipEntityFeesGate) {
      setEntityFeesGateOpen(true);
      return;
    }
    const doIt = async () => {
      if (purchasingRef.current) return;
      purchasingRef.current = true;
      setIsPurchasing(true);
      try {
        // Iter72: quantità è valida solo per acquisti a plafond su servizi che
        // permettono l'acquisto multiplo. Shared-with-client = 1 (single link).
        const allowsQty = !!selected?.allow_multiple_quantity && !form.is_shared_with_client;
        const qty = allowsQty ? Math.max(1, Math.min(10, Number(form.quantity) || 1)) : 1;
        const payload = {
          service_id: selected._id,
          ...form,
          quantity: qty,
          client_markup: Number(form.client_markup),
          scheduled_slot: (chosenSlot && !visitSkipped)
            ? { date: chosenSlot.date, hour: chosenSlot.hour, minute: Number(chosenSlot.minute || 0), operator_id: chosenSlot.best_operator.id }
            : null,
          chained_choices: chainedChoices,
          sub_margin_pct: isSubUser ? Number(subMarginPct) || 0 : 0,
          is_shared_with_client: isSubUser ? true : form.is_shared_with_client,
          visit_skipped: visitSkipped,
          // Iter66: checklist documenti + disclaimer
          document_checklist: documentChecklist,
          documents_disclaimer_accepted: documentsDisclaimerAcked,
          // Iter117: Prezzo Flessibile — risposte selezionate dal PRO
          pricing_answers: pricingAnswers,
          // Admin/supervisor marketplace: prenotazione per conto di un pro
          on_behalf_of_professional_id: onBehalfOfProId || undefined,
          professional_price_override: (
            onBehalfOfProId && !form.is_shared_with_client && !isSubUser
            && Number(form.professional_price_override) > 0
            && Number(form.professional_price_override) !== Number(adjustedProfessionalPrice)
          ) ? Number(form.professional_price_override) : undefined,
          ...(extraPayload || {}),
        };
        const r = await api.post("/practices/purchase", payload);
        // Iter72: response può essere {practices:[…], count:N} per qty>1 oppure
        // il singolo dict pratica per qty=1 (formato legacy). Normalizziamo.
        const isMulti = Array.isArray(r.data?.practices);
        const firstPractice = isMulti ? r.data.practices[0] : r.data;
        const createdCount = isMulti ? r.data.count : 1;
        if (extraPayload?.replaces_practice_id) toast.success("Pratica cambiata con successo");
        if (form.is_shared_with_client) {
          const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
          const fullUrl = `${base}/pay/${firstPractice._id}`;
          dispatch({
            type: "SET_FIELD",
            field: "shareLinkInfo",
            value: {
              url: fullUrl,
              practice_id: firstPractice._id,
              client_name: form.client_name,
              client_email: form.client_email,
            },
          });
          dispatch({ type: "SET_STAGE", stage: "share_link" });
          toast.success("Pratica creata. Link cliente pronto da condividere");
        } else {
          // Iter72/74: raccogliamo l'elenco delle pratiche create (quantità
          // multiple e/o chained-visit-group) per mostrarle nella Success view.
          const createdPractices = [];
          if (isMulti) {
            for (const pp of r.data.practices || []) {
              createdPractices.push({
                _id: pp._id, service_name: pp.service_name || selected?.name || "Servizio",
                visit_group_role: pp.visit_group_role || "",
              });
              // Espandi anche eventuali chained figli di ciascuna pratica
              for (const cid of pp.chained_practice_ids || []) {
                createdPractices.push({ _id: cid, service_name: "Servizio concatenato", visit_group_role: "chained" });
              }
            }
          } else {
            createdPractices.push({
              _id: firstPractice?._id,
              service_name: firstPractice?.service_name || selected?.name || "Servizio",
              visit_group_role: firstPractice?.visit_group_role || "",
            });
            for (const cid of firstPractice?.chained_practice_ids || []) {
              createdPractices.push({ _id: cid, service_name: "Servizio concatenato", visit_group_role: "chained" });
            }
          }
          const totalCount = createdPractices.length;
          if (totalCount > 1) {
            toast.success(`${totalCount} pratiche create con plafond mensile`);
          } else {
            toast.success("Servizio acquistato con plafond mensile");
          }
          dispatch({
            type: "PURCHASE_SUCCESS",
            info: {
              practice_id: firstPractice?._id,
              service_name: selected?.name || "il servizio",
              payment_method: "plafond",
              count: totalCount,
              practices: createdPractices,
            },
          });
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Errore");
      } finally {
        purchasingRef.current = false;
        setIsPurchasing(false);
      }
    };
    const label = form.is_shared_with_client
      ? "Genera link pagamento cliente"
      : "Acquisto con plafond mensile";
    promptIfDemo(doIt, { label });
  };

  const copyShareLink = async () => {
    if (!shareLinkInfo?.url) return;
    try {
      await navigator.clipboard.writeText(shareLinkInfo.url);
      toast.success("Link copiato negli appunti");
    } catch {
      const el = document.querySelector('[data-testid="share-link-input"]');
      if (el) {
        el.select();
        document.execCommand("copy");
        toast.success("Link copiato");
      } else toast.error("Impossibile copiare");
    }
  };

  const setStageAction = (s) => dispatch({ type: "SET_STAGE", stage: s });
  // Iter209e3 — Dopo immobile/requisiti si instrada in base all'intent scelto.
  const goToIntentTarget = () => {
    if (intent === "quote") {
      setStageAction("quote");
    } else if (isSubUser) {
      // Sub-user forzato in modalità "Condividi con cliente": azzera
      // slot/data per non trascinarli nella pratica condivisa.
      patchForm({ is_shared_with_client: true });
      dispatch({ type: "SET_FIELD", field: "chosenSlot", value: null });
      dispatch({ type: "SET_FIELD", field: "selectedDate", value: null });
      setStageAction("purchase");
    } else {
      setStageAction("payment_choice");
    }
  };
  // Iter209er — Stage "qty_visit" (sopralluogo sì/no + quantità) dopo i documenti.
  const showVisitChoice = !!selected?.can_skip_visit && visitOnFirstStep;
  const needsQtyVisit = intent === "quote" ? showVisitChoice : (showVisitChoice || !!selected?.allow_multiple_quantity);
  const hasRequirements = () =>
    (selected?.required_document_ids || []).length > 0 || chainedServicesDetails.length > 0;
  const afterQtyVisit = () => {
    // Preventivo: riallinea il prezzo proposto alla scelta sopralluogo se non modificato a mano.
    if (intent === "quote") {
      const cur = Number(quoteForm.price_excl_vat) || 0;
      const plain = Number(selected?.professional_price) || 0;
      const priced = [0, plain, computeUnitPrice(selected, computeVisitDelta(selected, visitProIds, true)), computeUnitPrice(selected, computeVisitDelta(selected, visitProIds, false))];
      if (priced.some((v) => Math.abs(v - cur) < 0.005)) patchQuoteForm({ price_excl_vat: (Number(adjustedProfessionalPrice) || 0).toFixed(2) });
    }
    if (hasRequirements()) setStageAction("requirements");
    else goToIntentTarget();
  };
  const backToBeforeRequirements = () => setStageAction(needsQtyVisit ? "qty_visit" : "property_docs");
  // "Indietro" da preventivo / scelta pagamento: torna ai requisiti o allo stage precedente.
  // Le risposte del Prezzo Flessibile (stage precedente) vanno conservate.
  const backFromIntentTarget = () => {
    if (hasRequirements()) setStageAction("requirements");
    else backToBeforeRequirements();
  };
  const patchForm = (patch) => dispatch({ type: "PATCH_FORM", patch });
  const patchQuoteForm = (patch) => dispatch({ type: "PATCH_QUOTE_FORM", patch });
  const patchNewPropForm = (patch) => dispatch({ type: "PATCH_NEW_PROP_FORM", patch });
  const setField = (field, value) => dispatch({ type: "SET_FIELD", field, value });

  const generateQuote = async () => {
    const net = Math.max(0, Number(quoteForm.price_excl_vat) || 0);
    // Iter140d: se il servizio ha Prezzo Flessibile attivo, blocca la
    // generazione finché tutte le domande non hanno una risposta.
    if (selected?.has_flexible_price && Array.isArray(selected?.pricing_logic) && selected.pricing_logic.length > 0) {
      if (!isFlexiblePriceComplete(selected, pricingAnswers)) {
        toast.error("Rispondi a tutte le domande del prezzo flessibile prima di generare il preventivo");
        return;
      }
    }
    const doIt = async () => {
      setField("generatingQuote", true);
      try {
        const resp = await api.post(
          "/quotes/generate",
          {
            service_id: selected._id,
            property_id: form.property_id,
            price_excl_vat: net,
            vat_rate: 22,
            notes: quoteForm.notes,
            // Iter140d: invia le risposte del Prezzo Flessibile al backend,
            // così il PDF include il riepilogo delle scelte e delle maggiorazioni.
            pricing_answers: pricingAnswers,
            visit_skipped: showVisitChoice ? visitSkipped : null,
          },
          { responseType: "blob" },
        );
        const blob = new Blob([resp.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Preventivo_${selected.name.replace(/[^\w\s-]/g, "").slice(0, 40)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success("Preventivo generato");
        onQuotesChanged?.();
        onClose?.();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Errore generazione preventivo");
      } finally {
        setField("generatingQuote", false);
      }
    };
    promptIfDemo(doIt, { label: "Generazione preventivo PDF" });
  };

  // Iter209e1 — Chiusura definitiva (azzera Prezzo Flessibile, Iter127).
  const closeModal = () => {
    setExitConfirmOpen(false);
    resetPricing();
    onClose?.();
  };
  // Dati già compilati = il pro è andato oltre la schermata iniziale
  // (immobile, requisiti, pagamento...) o ha risposto al Prezzo Flessibile.
  const hasUnsavedData = !["choice", "info", "success", "share_link"].includes(stage) || pricingAnswers.length > 0;
  const requestClose = () => {
    if (hasUnsavedData) setExitConfirmOpen(true);
    else closeModal();
  };

  return (
    <>
    <Dialog
      open={!!selected}
      onOpenChange={(o) => { if (!o) requestClose(); }}
    >
      <DialogContent
        // Iter209e5 — Altezza max 90vh: header fisso, scrolla solo il corpo
        // degli stage (il dialog non mostra mai la propria scrollbar).
        className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        // Chiusura solo con X / Annulla: niente click fuori né tasto Esc.
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="purchase-modal"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{selected?.name}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-6 px-6" data-testid="purchase-modal-body">

        {selected && stage === "info" && (
          <div className="space-y-4 py-2" data-testid="stage-info">
            <div className="p-4 rounded-md border-2 border-purple-600 bg-purple-50" data-testid="svc-housetag-notice">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full ht-gradient flex-shrink-0 flex items-center justify-center text-white font-bold">
                  i
                </div>
                <div className="text-sm text-slate-800 leading-relaxed">
                  <div className="font-semibold text-purple-900 mb-1">Attenzione</div>
                  Housetag avrà accesso alla scheda del tuo immobile. Nei prossimi passaggi ti verranno mostrati
                  i requisiti (documenti e servizi prerequisiti) necessari all'esecuzione del servizio.
                </div>
              </div>
            </div>

            {/* Iter85: rimosso il blocco documenti dalla fase 'info' — i requisiti
                (documenti + servizi concatenati) appaiono ora una sola volta nella
                schermata UnifiedRequirementsStage dopo la scelta dell'immobile. */}
            {(selected.client_info || "").trim() && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-sm text-purple-700 hover:text-purple-900 font-medium rounded-full px-2 py-1 -ml-2 hover:bg-purple-50 transition-colors"
                    data-testid="svc-client-info-btn"
                  >
                    <Info size={18} weight="regular" />
                    Informazioni cliente
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-80 max-h-72 overflow-y-auto z-[200]"
                  data-testid="svc-client-info-popover"
                >
                  <div className="text-[10px] uppercase tracking-widest text-purple-800 font-bold mb-2">
                    Informazioni per il cliente
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{selected.client_info}</div>
                </PopoverContent>
              </Popover>
            )}

            <div className="flex justify-start pt-1">
              <button
                type="button"
                onClick={() => ((intent === "quote" || intent === "purchase") && openIntent?.stage === "info" ? onClose() : setStageAction("choice"))}
                className="text-xs text-muted-foreground hover:text-foreground"
                data-testid="info-back-btn"
              >
                ← Indietro
              </button>
            </div>
            <Button
              className="w-full ht-gradient border-0"
              onClick={() => {
                // Iter209g — Se il servizio ha prezzo flessibile, prima di
                // scegliere l'immobile mostriamo il wizard di preventivo.
                if (
                  selected?.has_flexible_price
                  && Array.isArray(selected.pricing_logic)
                  && selected.pricing_logic.length > 0
                ) {
                  setStageAction("flexible_price");
                } else {
                  setStageAction("property");
                }
              }}
              data-testid="info-continue-btn"
            >
              Continua →
            </Button>
          </div>
        )}

        {/* Iter209g — Stage prezzo flessibile: appare tra "info" e
            "property" solo se il servizio ha domande dinamiche. */}
        {selected && stage === "flexible_price" && (
          <div className="space-y-4 py-2" data-testid="stage-flexible-price">
            <div>
              <h4 className="font-display text-lg font-semibold mb-1">
                Personalizza il tuo preventivo
              </h4>
              <p className="text-sm text-muted-foreground">
                Rispondi a queste domande per ottenere un prezzo su misura per il tuo immobile.
              </p>
            </div>
            <FlexiblePriceWizard
              pricingLogic={selected.pricing_logic}
              basePrice={Number(selected.base_price) || 0}
              pricingAnswers={pricingAnswers}
              setPricingAnswers={setPricingAnswers}
              onDeltaChange={() => { /* delta letto direttamente dal calcolo sotto */ }}
              autoCommitMulti
            />
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-slate-50" data-testid="flexible-price-preview">
              <span className="text-sm text-slate-700">Prezzo stimato</span>
              <span className="font-mono font-semibold text-slate-900 whitespace-nowrap" data-testid="flexible-price-preview-total">
                {fmtPrice(computeUnitPrice(selected, computeFlexibleDelta(selected, pricingAnswers) + visitDelta))}
                <span className="ml-1 text-[0.7em] font-sans font-normal text-slate-500">+ IVA</span>
              </span>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setStageAction("info")}
                data-testid="flexible-price-back-btn"
              >
                ← Indietro
              </Button>
              <Button
                className="flex-1 ht-gradient border-0"
                onClick={() => setStageAction("property")}
                disabled={!isFlexiblePriceComplete(selected, pricingAnswers)}
                data-testid="flexible-price-continue-btn"
              >
                Continua →
              </Button>
            </div>
          </div>
        )}

        {selected && stage === "property" && (
          <PropertyStage
            selected={selected}
            properties={properties}
            form={form}
            propSearch={propSearch}
            createMode={createMode}
            newPropForm={newPropForm}
            creatingProp={creatingProp}
            onPatchForm={patchForm}
            onSetPropSearch={(v) => setField("propSearch", v)}
            onSetCreateMode={(v) => setField("createMode", v)}
            onPatchNewPropForm={patchNewPropForm}
            onSubmitNewProperty={submitNewProperty}
            clientsProId={onBehalfOfProId}
            onBack={() => {
              // Torna allo stage precedente: Prezzo Flessibile se il servizio
              // lo prevede, altrimenti info (o choice se aperto direttamente qui).
              const hasFlex = selected?.has_flexible_price
                && Array.isArray(selected.pricing_logic) && selected.pricing_logic.length > 0;
              setStageAction(hasFlex ? "flexible_price" : (intent ? "info" : "choice"));
            }}
            onNext={() => setStageAction("property_docs")}
          />
        )}

        {selected && stage === "property_docs" && (
          <PropertyDocsStage
            propertyId={form.property_id}
            files={property_docs_files}
            onSetFiles={(v) => setField("property_docs_files", v)}
            onBack={() => setStageAction("property")}
            onNext={() => (needsQtyVisit ? setStageAction("qty_visit") : afterQtyVisit())}
          />
        )}

        {selected && stage === "requirements" && (
          <UnifiedRequirementsStage
            selected={selected}
            documentCatalog={documentCatalog}
            servicesById={new Map(chainedServicesDetails.map(s => [s._id, s]))}
            documentChecklist={documentChecklist}
            onDocumentToggle={(id, checked) => {
              setDocumentChecklist({ ...documentChecklist, [id]: checked });
              // Iter131: NON resettare `documentsDisclaimerAcked` al check di un
              // singolo documento — il flag va invalidato solo se cambia il
              // servizio (già gestito in `useEffect` più sopra), non a ogni
              // spunta. Se lo resettassimo qui, il disclaimer riappare durante
              // la compilazione dando l'impressione di un bug.
            }}
            chainedChoices={chainedChoices}
            onChainedChoice={(id, choice) => dispatch({ type: "SET_CHAINED_CHOICE", id, choice })}
            documentsDisclaimerAcked={documentsDisclaimerAcked}
            onDocsDisclaimerRequired={() => setDocsAckOpen(true)}
            onContinue={goToIntentTarget}
            onBack={backToBeforeRequirements}
            fmtPrice={fmtPrice}
            /* Iter209ab — Passiamo i supplementi accumulati nel flusso prezzo
               flessibile così l'"Anteprima Costi" possa mostrarli come righe
               separate e includerli nel totale. */
            flexibleSupplements={computeFlexibleSupplements(selected, pricingAnswers)}
          />
        )}

        {selected && stage === "chained_prereq" && (
          <ChainedPrereqStage
            selected={selected}
            chainedServicesDetails={chainedServicesDetails}
            chainedChoices={chainedChoices}
            onSetChoice={(id, choice) => dispatch({ type: "SET_CHAINED_CHOICE", id, choice })}
            onBack={() => { resetPricing(); setStageAction("property"); }}
            onNext={goToIntentTarget}
          />
        )}

        {selected && stage === "choice" && (
          <div className="space-y-4 py-2" data-testid="stage-choice">
            <p className="text-sm text-muted-foreground">Come vuoi procedere con "{selected.name}"?</p>
            <div className="grid grid-cols-1 gap-3">
              <button
                type="button"
                onClick={() => { setField("intent", "quote"); setStageAction("info"); }}
                className="text-left p-4 border-2 border-dashed rounded-lg hover:border-purple-400 hover:bg-purple-50/50 transition-all"
                data-testid="stage-quote"
              >
                <div className="font-semibold text-purple-800">📄 Crea preventivo</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Genera un PDF con la tua intestazione e il prezzo maggiorato del margine
                </div>
              </button>
              <button
                type="button"
                onClick={() => { setField("intent", "purchase"); setStageAction("info"); }}
                className="text-left p-4 border-2 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
                data-testid="stage-purchase"
              >
                <div className="font-semibold text-purple-800">✓ Prosegui con l'acquisto</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {isSubUser
                    ? "Genera link di pagamento per il cliente finale"
                    : "Attiva subito il servizio e assegnalo all'immobile"}
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Iter209er — Stage "qty_visit": scelta sopralluogo (obbligatoria se
            saltabile) + quantità, prima dei requisiti. */}
        {selected && stage === "qty_visit" && (
          <div className="space-y-4 py-2" data-testid="stage-qty-visit">
            {showVisitChoice && (
              <div className="space-y-2" data-testid="visit-choice-section">
                <div className="text-sm font-semibold text-slate-800">Sopralluogo</div>
                <p className="text-sm text-slate-600">È necessario il sopralluogo?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { key: "yes", label: "✅ Sì, effettua il sopralluogo" },
                    { key: "no", label: "⏭️ No, salta il sopralluogo" },
                  ].map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      role="radio"
                      aria-checked={visitDecision === o.key}
                      onClick={() => {
                        setVisitDecision(o.key);
                        if (o.key === "no") {
                          setVisitSkipped(true);
                          dispatch({ type: "SET_FIELD", field: "chosenSlot", value: null });
                          dispatch({ type: "SET_FIELD", field: "selectedDate", value: null });
                        } else {
                          setVisitSkipped(false);
                        }
                      }}
                      className={`text-left rounded-lg border-2 p-3 text-sm font-medium transition-colors ${visitDecision === o.key
                        ? (o.key === "no" ? "border-amber-500 bg-amber-50 text-amber-900" : "border-emerald-500 bg-emerald-50 text-emerald-900")
                        : "border-slate-200 hover:border-slate-300 text-slate-700"}`}
                      data-testid={`visit-choice-${o.key}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {visitDecision === "no" && (
                  <div className="p-3 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-xs leading-relaxed" data-testid="visit-skip-warning">
                    <strong>Attenzione:</strong> Housetag e i tecnici <strong>non saranno responsabili</strong> per errori o
                    informazioni mancanti dovute all'assenza del sopralluogo. La lavorazione partirà dallo step successivo.
                  </div>
                )}
                {selected.skip_visit_note && (
                  <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1.5" data-testid="skip-visit-note">
                    <span className="font-semibold">Nota:</span> {selected.skip_visit_note}
                  </div>
                )}
              </div>
            )}

            {/* Iter72: selettore quantità per acquisti a plafond su servizi
                configurati con `allow_multiple_quantity`. Non applicabile alla
                condivisione con cliente (link Stripe singolo). */}
            {selected.allow_multiple_quantity && intent !== "quote" && (
              <div className="p-3 border rounded-lg bg-emerald-50/40 border-emerald-200" data-testid="quantity-selector-row">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-emerald-900">🔢 Quantità</div>
                    <div className="text-[11px] text-emerald-800/80">
                      Acquistando più unità creerai N pratiche separate, ognuna con il proprio addebito plafond.
                    </div>
                    <div
                      className="text-[11px] italic text-slate-600 mt-1"
                      data-testid="quantity-discount-note"
                    >
                      N.B. lo sconto quantità è valido per servizio relativo a stesso immobile con subalterni differenti.
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-1 bg-white rounded-md border border-emerald-300 p-1">
                    <button
                      type="button"
                      onClick={() => patchForm({ quantity: Math.max(1, (Number(form.quantity) || 1) - 1) })}
                      className="w-8 h-8 rounded hover:bg-emerald-100 text-emerald-900 font-bold disabled:opacity-40"
                      disabled={(Number(form.quantity) || 1) <= 1}
                      data-testid="quantity-decrease"
                      aria-label="Diminuisci quantità"
                    >−</button>
                    <span
                      className="min-w-[2.5rem] text-center font-mono font-semibold text-emerald-900"
                      data-testid="quantity-value"
                    >{Number(form.quantity) || 1}</span>
                    <button
                      type="button"
                      onClick={() => patchForm({ quantity: Math.min(10, (Number(form.quantity) || 1) + 1) })}
                      className="w-8 h-8 rounded hover:bg-emerald-100 text-emerald-900 font-bold disabled:opacity-40"
                      disabled={(Number(form.quantity) || 1) >= 10}
                      data-testid="quantity-increase"
                      aria-label="Aumenta quantità"
                    >+</button>
                  </div>
                </div>
              </div>
            )}

            {(() => {
              const unit = computeUnitPrice(selected, computeFlexibleDelta(selected, pricingAnswers) + visitDelta);
              const qty = selected.allow_multiple_quantity ? Math.max(1, Number(form.quantity) || 1) : 1;
              let pct = 0;
              if (qty > 1 && Array.isArray(selected.quantity_discount_tiers)) {
                for (const t of selected.quantity_discount_tiers) {
                  const m = Number(t?.min_qty) || 0;
                  const p = Number(t?.discount_pct) || 0;
                  if (m > 0 && qty >= m && p > pct) pct = p;
                }
                pct = Math.min(50, Math.max(0, pct));
              }
              const discUnit = pct > 0 ? Math.ceil(Math.round(unit * (1 - pct / 100) * 100) / 100 / 5) * 5 : unit;
              const total = Number(adjustedProfessionalPrice) || 0;
              return (
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-slate-50" data-testid="pre-payment-price-preview">
                  <div className="text-sm text-slate-700">
                    {qty > 1 ? (
                      <span data-testid="pre-payment-price-breakdown">
                        {qty} × {fmtPrice(discUnit)}
                        {discUnit < unit && <span className="ml-1 text-emerald-700 font-medium">(-{pct}%)</span>}
                      </span>
                    ) : "Prezzo servizio"}
                  </div>
                  <div className="font-mono font-semibold text-slate-900 whitespace-nowrap" data-testid="pre-payment-price-total">
                    {fmtPrice(total)}<span className="ml-1 text-[0.7em] font-sans font-normal text-slate-500">+ IVA</span>
                  </div>
                </div>
              );
            })()}

            <Button
              className="w-full ht-gradient border-0"
              onClick={afterQtyVisit}
              disabled={showVisitChoice && !visitDecision}
              data-testid="qty-visit-continue-btn"
            >
              Continua →
            </Button>
            <div className="flex justify-start">
              <button
                type="button"
                onClick={() => setStageAction("property_docs")}
                className="text-xs text-muted-foreground hover:text-foreground"
                data-testid="qty-visit-back-btn"
              >
                ← Indietro
              </button>
            </div>
          </div>
        )}

        {selected && stage === "payment_choice" && (
          <div className="space-y-4 py-2" data-testid="stage-payment-choice">
            <p className="text-sm text-muted-foreground">Come vuoi pagare "{selected.name}"?</p>

            {/* Iter209g — Il FlexiblePriceWizard è stato spostato nello
                stage "flexible_price" (subito dopo "info"), così il
                cliente configura il preventivo prima di scegliere
                immobile e metodo di pagamento. */}

            {/* Iter82: preview completa con sconto tier + servizi chained purchase */}
            {(() => {
              // Iter117: applica delta Prezzo Flessibile al prezzo unitario base.
              // Ricomputo il delta qui (stessa formula del backend) per rispecchiarlo
              // istantaneamente in UI senza dipendere dallo stato asincrono del wizard.
              const flexDelta = computeFlexibleDelta(selected, pricingAnswers);
              const unit = computeUnitPrice(selected, flexDelta + visitDelta);
              const qty = selected.allow_multiple_quantity ? Math.max(1, Number(form.quantity) || 1) : 1;
              // Tier discount matching locale (Iter73)
              let discountPct = 0;
              if (qty > 1 && Array.isArray(selected.quantity_discount_tiers)) {
                for (const t of selected.quantity_discount_tiers) {
                  const m = Number(t?.min_qty) || 0;
                  const p = Number(t?.discount_pct) || 0;
                  if (m > 0 && qty >= m && p > discountPct) discountPct = p;
                }
                discountPct = Math.min(50, Math.max(0, discountPct));
              }
              const discountedUnit = discountPct > 0
                ? Math.ceil(Math.round(unit * (1 - discountPct / 100) * 100) / 100 / 5) * 5
                : unit;
              const primaryTotal = discountedUnit * qty;
              const primarySavings = qty > 1 ? (unit * qty - primaryTotal) : 0;

              // Chained purchases
              const purchasedChained = (chainedServicesDetails || []).filter(
                s => chainedChoices?.[s._id] === "purchase",
              );
              const chainedTotal = purchasedChained.reduce(
                (sum, s) => sum + Number(s.professional_price || s.base_price || 0),
                0,
              );
              const grandTotal = primaryTotal + chainedTotal;

              // Mostro la preview solo se c'è qualcosa di interessante (qty>1 o chained purchase)
              if (qty === 1 && purchasedChained.length === 0) return null;

              // Suggerimento tier successivo (leva conversione)
              let nextTierHint = null;
              if (selected.allow_multiple_quantity && Array.isArray(selected.quantity_discount_tiers)) {
                const above = selected.quantity_discount_tiers
                  .map(t => ({ m: Number(t.min_qty) || 0, p: Number(t.discount_pct) || 0 }))
                  .filter(t => t.m > qty && t.p > discountPct)
                  .sort((a, b) => a.m - b.m);
                if (above.length > 0) {
                  const missing = above[0].m - qty;
                  nextTierHint = `+${missing} ${missing === 1 ? "unità" : "unità"} per sbloccare -${above[0].p}%`;
                }
              }

              return (
                <div className="p-3 rounded-md border-l-4 border-purple-500 bg-white shadow-sm" data-testid="cost-preview-full">
                  <div className="text-[10px] uppercase tracking-widest text-purple-800 font-bold mb-2">
                    Anteprima costi
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-slate-700">
                        {selected.name}
                        {qty > 1 && <span className="text-slate-500"> · {qty} × {fmtPrice(discountedUnit)}</span>}
                        {discountPct > 0 && (
                          <span className="ml-1 text-emerald-700 font-semibold text-xs" data-testid="cost-preview-tier-badge">
                            −{discountPct}%
                          </span>
                        )}
                      </span>
                      <span className="font-mono whitespace-nowrap">{fmtPrice(primaryTotal)}<span className="ml-1 text-[0.7em] font-sans font-normal text-slate-500" data-testid="cost-preview-primary-iva">+ IVA</span></span>
                    </div>
                    {primarySavings > 0 && (
                      <div className="text-[11px] text-emerald-700" data-testid="cost-preview-savings">
                        Risparmi {fmtPrice(primarySavings)} rispetto al prezzo pieno
                      </div>
                    )}
                    {purchasedChained.map(s => (
                      <div key={s._id} className="flex justify-between text-emerald-700" data-testid={`cost-preview-chained-${s._id}`}>
                        <span>+ {s.name}</span>
                        <span className="font-mono whitespace-nowrap">{fmtPrice(Number(s.professional_price || s.base_price || 0))}<span className="ml-1 text-[0.7em] font-sans font-normal text-slate-500">+ IVA</span></span>
                      </div>
                    ))}
                    <div className="border-t pt-1 mt-1 flex justify-between items-center font-semibold text-slate-900">
                      <span>Totale</span>
                      <span className="font-mono text-purple-900 text-base" data-testid="cost-preview-grand-total">
                        {fmtPrice(grandTotal)}<span className="ml-1 text-[0.7em] font-sans font-normal text-slate-500" data-testid="cost-preview-total-iva">+ IVA</span>
                      </span>
                    </div>
                    {nextTierHint && (
                      <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-2" data-testid="cost-preview-next-tier-hint">
                        💡 {nextTierHint}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Iter132: riepilogo totale pratica sopra i tasti di pagamento.
                Include le maggiorazioni/sconti del Prezzo Flessibile
                (`adjustedProfessionalPrice`) così il PRO vede subito quanto
                pagherà — indipendentemente dal metodo scelto.
                Iter140f: mostriamo sia il totale netto (per il PRO plafond)
                sia il totale IVA 22% inclusa (quello che pagherà il cliente
                finale se condivido il link). */}
            {/* Iter209ea — Riepilogo: solo prezzo base del servizio + IVA. */}
            <div
              className="mb-4 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3"
              data-testid="payment-total-recap"
            >
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-indigo-800">Imponibile (Plafond PRO)</span>
                <span className="font-mono text-sm text-indigo-900 tabular-nums" data-testid="payment-total-net">
                  {fmtPrice(Number(adjustedProfessionalPrice) || 0)}<span className="ml-1 text-[0.7em] font-sans font-normal text-slate-500" data-testid="payment-total-net-iva">+ IVA</span>
                </span>
              </div>
            </div>

            {/* Iter140f — SEZIONE A: "Condividi al tuo cliente" (sempre visibile).
                Genera un link di pagamento pubblico dove il cliente sceglierà
                il proprio metodo (Stripe, Bonifico, …). Il totale mostrato è
                lordo (IVA 22% inclusa). */}
            {appSettings.enable_share_with_client !== false && (
            <div className="space-y-2" data-testid="section-share-client">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                Sezione A — Condividi con il cliente
              </div>
              <button
                type="button"
                onClick={() => {
                  patchForm({ is_shared_with_client: true });
                  dispatch({ type: "SET_FIELD", field: "chosenSlot", value: null });
                  dispatch({ type: "SET_FIELD", field: "selectedDate", value: null });
                  setStageAction("purchase");
                }}
                className="w-full text-left p-4 border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg hover:border-purple-500 hover:shadow-md transition-all"
                data-testid="payment-mode-share"
              >
                <div className="font-semibold text-purple-800 flex items-center gap-2">
                  <span>🔗</span>
                  {/* Iter208.27 — Label fissa lato frontend: nel contesto PRO
                      questa card significa "condividi con il cliente".
                      Il nome DB (`Carta di Credito/Debito`) è mostrato
                      solo nella lista pagamenti admin. */}
                  <span>Condividi al tuo cliente</span>
                  <CardInfoPopover testId="payment-mode-share-info">
                    <div data-testid="payment-mode-share-desc">
                      Applica un margine e invia link di pagamento al cliente. Il tuo margine sarà accumulato
                      come credito sulla tua dashboard. Attenzione: è previsto un contributo di rifatturazione.
                    </div>
                    {/* Iter209eb — Pagamento in 2 rate: anticipo/saldo sul lordo
                        IVA inclusa (prezzo + contributo, esclusa la maggiorazione). */}
                    {!!selected?.split_payment_enabled && !demo() && (() => {
                      const gross = Math.round((Number(adjustedProfessionalPrice) || 0) * (1 + housingFeeRate) * 1.22 * 100) / 100;
                      const advPct = Math.max(1, Math.min(99, Number(selected?.advance_percentage) || 0));
                      const advGross = Math.round(gross * advPct) / 100;
                      const balGross = Math.round((gross - advGross) * 100) / 100;
                      return (
                        <div className="mt-2 pt-2 border-t border-slate-200 space-y-1" data-testid="share-split-breakdown">
                          <div className="font-semibold text-purple-800">Pagamento in 2 rate</div>
                          <div className="flex justify-between gap-2">
                            <span>Anticipo ({advPct}%)</span>
                            <span className="font-mono font-semibold" data-testid="share-split-advance">{fmtPrice(advGross)}</span>
                          </div>
                          <div className="flex justify-between gap-2">
                            <span>Saldo ({100 - advPct}%)</span>
                            <span className="font-mono font-semibold" data-testid="share-split-balance">{fmtPrice(balGross)}</span>
                          </div>
                          <div className="text-[10px] text-slate-500 italic">
                            Importi IVA inclusa, esclusa la tua maggiorazione. Il cliente paga l'anticipo per attivare la pratica e il saldo prima della consegna.
                          </div>
                        </div>
                      );
                    })()}
                  </CardInfoPopover>
                </div>
              </button>
            </div>
            )}

            {/* Iter140f — SEZIONE B: "Pagamento diretto" (metodi PRO).
                Elenca dinamicamente i metodi con `visible_to_pro=true` filtrati
                dal backend. Plafond è pienamente funzionante; per altri
                (bonifico / stripe carta diretta) il backend supporta oggi solo
                plafond quindi mostriamo un badge "solo plafond attivo" se
                l'admin ha abilitato altri metodi senza il rispettivo flow. */}
            <div className="space-y-2 mt-4" data-testid="section-direct-pay">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                Sezione B — Paga tu direttamente
              </div>
              <div className="grid grid-cols-1 gap-3" data-testid="payment-mode-buttons">
                {directMethods
                  .map((m) => {
                    const isPlafond = m.type === "plafond";
                    // Iter140f: oggi il backend supporta solo `plafond` come
                    // acquisto diretto PRO. Per metodi diversi mostriamo la
                    // card grigia con un badge "presto disponibile".
                    if (!isPlafond) {
                      return (
                        <div
                          key={m._id || m.id || m.type}
                          className="p-4 border-2 border-slate-200 bg-slate-50 rounded-lg opacity-70"
                          data-testid={`payment-mode-${m.type}-disabled`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-slate-700">
                              💼 {m.name || m.type}
                            </div>
                            <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-0.5">
                              Presto disponibile
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            Il pagamento diretto PRO via {m.name || m.type} sarà
                            attivato a breve. Per ora usa il Plafond o condividi
                            con il cliente.
                          </div>
                        </div>
                      );
                    }
                    return (
                      <button
                        key={m._id || m.id || m.type}
                        type="button"
                        onClick={() => {
                          patchForm({ is_shared_with_client: false });
                          setStageAction("purchase");
                        }}
                        className="text-left p-4 border-2 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
                        data-testid="payment-mode-plafond"
                      >
                        <div className="font-semibold text-purple-800 flex items-center gap-2">
                          <span>💳 {m.name || "Acquista con il tuo plafond"}</span>
                          {!demo() && (
                            <CardInfoPopover testId="payment-mode-plafond-info">
                        {(() => {
                          const flexDelta = computeFlexibleDelta(selected, pricingAnswers);
                          const unit = computeUnitPrice(selected, flexDelta + visitDelta);
                          const qty = selected.allow_multiple_quantity ? Math.max(1, Number(form.quantity) || 1) : 1;
                          let discountPct = 0;
                          if (qty > 1 && Array.isArray(selected.quantity_discount_tiers)) {
                            for (const t of selected.quantity_discount_tiers) {
                              const min = Number(t?.min_qty) || 0;
                              const p = Number(t?.discount_pct) || 0;
                              if (min > 0 && qty >= min && p > discountPct) discountPct = p;
                            }
                            discountPct = Math.min(50, Math.max(0, discountPct));
                          }
                          const discountedUnit = discountPct > 0
                            ? Math.ceil(Math.round(unit * (1 - discountPct / 100) * 100) / 100 / 5) * 5
                            : unit;
                          const total = discountedUnit * qty;
                          return (
                            <div data-testid="payment-mode-plafond-desc">
                              Addebito {fmtPrice(total)}{" "}
                              <span data-testid="payment-mode-plafond-iva">+ IVA</span>
                              {qty > 1 && (
                                <> ({qty} × {fmtPrice(discountedUnit)}
                                  {discountPct > 0 && (
                                    <span className="text-emerald-700 font-semibold ml-1" data-testid="qty-discount-badge">
                                      −{discountPct}%
                                    </span>
                                  )}
                                  )
                                </>
                              )}
                              {" "}sul plafond mensile · pratica attiva subito
                              {selected.requires_visit && !visitSkipped ? " · scegli lo slot di sopralluogo" : ""}
                            </div>
                          );
                        })()}
                            </CardInfoPopover>
                          )}
                        </div>
                      </button>
                    );
                  })}
                {appSettings.enable_share_with_client === false && !directMethods.some((m) => m.type === "plafond") && (
                  <div
                    className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50 text-sm text-amber-900"
                    data-testid="payment-modes-none-active"
                  >
                    Al momento non ci sono modalità di acquisto attive. Contatta l'amministratore.
                  </div>
                )}
                {directMethods.length === 0 && (
                  <div
                    className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50 text-sm text-amber-900"
                    data-testid="payment-modes-empty"
                  >
                    ⚠️ Nessun metodo di pagamento diretto è attivo.
                    Contatta l'amministratore per abilitare Plafond o un altro
                    metodo PRO.
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={backFromIntentTarget}
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid="payment-choice-back-btn"
            >
              ← Indietro
            </button>
          </div>
        )}

        {selected && stage === "quote" && (
          <QuoteStage
            selected={selected}
            properties={properties}
            form={form}
            quoteForm={quoteForm}
            generatingQuote={generatingQuote}
            onPatchQuoteForm={patchQuoteForm}
            onBack={backFromIntentTarget}
            onGenerate={generateQuote}
            /* Iter140d: prezzo flessibile nel flusso preventivi */
            pricingAnswers={pricingAnswers}
            setPricingAnswers={setPricingAnswers}
            adjustedProfessionalPrice={adjustedProfessionalPrice}
            flexReady={isFlexiblePriceComplete(selected, pricingAnswers)}
            housingFeeRate={housingFeeRate}
          />
        )}

        {selected && stage === "purchase" && (
          <PurchaseStage
            selected={selected}
            properties={properties}
            user={user}
            plafond={plafond}
            isSubUser={isSubUser}
            form={form}
            subMarginPct={subMarginPct}
            visitOnFirstStep={visitOnFirstStep}
            slotMonth={slotMonth}
            monthGrid={monthGrid}
            selectedDate={selectedDate}
            slotsForSelectedDate={slotsForSelectedDate}
            chosenSlot={chosenSlot}
            totalSlotsCount={totalSlotsCount}
            onSubmit={purchase}
            isPurchasing={isPurchasing}
            onBackToPayment={isSubUser ? backFromIntentTarget : () => setStageAction("payment_choice")}
            onBackToProperty={() => setStageAction("property")}
            onPatchForm={patchForm}
            onSetSubMargin={(v) => setField("subMarginPct", v)}
            onSetSlotMonth={(m) => dispatch({ type: "PICK_MONTH", month: m })}
            onSetSelectedDate={(d) => dispatch({ type: "PICK_DATE", date: d })}
            onSetChosenSlot={(s) => setField("chosenSlot", s)}
            canSkipVisit={!!selected.can_skip_visit && visitOnFirstStep}
            skipVisitNote={selected.skip_visit_note || ""}
            visitSkipped={visitSkipped}
            totalPrice={adjustedProfessionalPrice}
            housingFeeRate={housingFeeRate}
            canOverridePrice={!!onBehalfOfProId && !form.is_shared_with_client && !isSubUser}
          />
        )}

        {/* Iter66: modal di conferma responsabilità documenti mancanti */}
        {docsAckOpen && (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4" data-testid="docs-disclaimer-modal">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📄</span>
                <h3 className="font-display text-lg font-bold">Documenti mancanti</h3>
              </div>
              <p className="text-sm text-slate-800">
                Alcuni documenti richiesti non sono stati confermati come disponibili.
              </p>
              <div className="p-3 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-sm">
                <strong>Consapevole</strong> che il servizio potrà essere completato solo alla ricezione dei documenti richiesti. <strong>Sei consapevole del rischio?</strong>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold" data-testid="docs-disclaimer-checkbox-row">
                <input
                  type="checkbox"
                  checked={documentsDisclaimerAcked}
                  onChange={(e) => setDocumentsDisclaimerAcked(e.target.checked)}
                  data-testid="docs-disclaimer-checkbox"
                />
                SÌ, sono consapevole e voglio procedere
              </label>
              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setDocsAckOpen(false)}
                  className="px-4 py-2 rounded border text-sm hover:bg-slate-50"
                  data-testid="docs-disclaimer-cancel"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  disabled={!documentsDisclaimerAcked}
                  onClick={() => {
                    setDocsAckOpen(false);
                    // Iter131/209e3: dopo il disclaimer sui documenti mancanti
                    // si prosegue verso preventivo o scelta pagamento (intent).
                    goToIntentTarget();
                  }}
                  className="px-4 py-2 rounded bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="docs-disclaimer-confirm"
                >
                  Procedi
                </button>
              </div>
            </div>
          </div>
        )}


        {selected && stage === "share_link" && shareLinkInfo && (
          <ShareLinkStage
            shareLinkInfo={shareLinkInfo}
            onCopy={copyShareLink}
            onClose={onClose}
          />
        )}

        {selected && stage === "success" && (
          <div className="py-6 text-center" data-testid="stage-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h3 className="font-display text-2xl font-bold tracking-tight mb-2">
              {(successInfo?.count || 1) > 1
                ? <>Complimenti! Hai creato {successInfo.count} pratiche</>
                : <>Complimenti! Hai appena prenotato il tuo servizio</>}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {successInfo?.service_name
                ? <>Trovi <strong>“{successInfo.service_name}”</strong> tra i servizi in lavorazione.</>
                : "Trovi il servizio tra quelli in lavorazione."}
            </p>

            {/* Iter74: elenco pratiche create (quantità multiple e/o chained group) */}
            {Array.isArray(successInfo?.practices) && successInfo.practices.length > 1 && (
              <div className="text-left border rounded-lg p-3 mb-4 bg-slate-50" data-testid="success-practices-list">
                <div className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold mb-2">
                  Riepilogo pratiche create
                </div>
                <ul className="space-y-1.5">
                  {successInfo.practices.map((pp, i) => (
                    <li key={pp._id || i} className="flex items-center justify-between gap-2 text-sm" data-testid={`success-practice-${i}`}>
                      <span className="truncate text-slate-800">
                        {pp.visit_group_role === "chained" && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mr-2">Concatenata</span>}
                        {pp.visit_group_role === "primary" && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 mr-2">Primaria</span>}
                        {pp.service_name || "Servizio"}
                      </span>
                      {pp._id && (
                        <a href={`/practices/${pp._id}`} className="text-xs text-purple-700 hover:underline font-medium shrink-0" data-testid={`success-practice-link-${i}`}>
                          Apri →
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              className="w-full ht-gradient border-0 h-11"
              data-testid="purchase-success-go-in-progress"
              onClick={() => {
                onPurchaseSuccess?.(successInfo);
                onClose?.();
              }}
            >
              Vai ai servizi in lavorazione
            </Button>
          </div>
        )}
        </div>
      </DialogContent>

      {/* GATE DIRITTI ENTE — informativa mostrata al pro quando il servizio
          prevede oneri Ente. Confermare per proseguire con l'acquisto. */}
      <Dialog open={entityFeesGateOpen} onOpenChange={(o) => { if (!o) setEntityFeesGateOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="entity-fees-gate">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              💶 Diritti ente da pagare
            </DialogTitle>
            <DialogDescription>
              Questo servizio prevede il pagamento di oneri all'Ente. Leggi le condizioni prima di procedere.
            </DialogDescription>
          </DialogHeader>
          <div
            className="text-sm text-slate-700 leading-relaxed py-2 max-h-[50vh] overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: entityFeesPolicyHtml || "<em>Nessuna informativa disponibile.</em>" }}
            data-testid="entity-fees-gate-content"
          />
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setEntityFeesGateOpen(false)}
              data-testid="entity-fees-gate-cancel"
            >
              Annulla
            </Button>
            <Button
              className="ht-gradient border-0 text-white"
              onClick={() => {
                setEntityFeesAcked(true);
                setEntityFeesGateOpen(false);
                // Chiamata esplicita che salta il gate (evita stale closure su entityFeesAcked)
                purchase(null, { skipEntityFeesGate: true });
              }}
              data-testid="entity-fees-gate-confirm"
            >
              Ho letto e proseguo →
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
    <AlertDialog open={exitConfirmOpen} onOpenChange={setExitConfirmOpen}>
      <AlertDialogContent className="z-[120]" data-testid="purchase-exit-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Vuoi uscire dall'acquisto?</AlertDialogTitle>
          <AlertDialogDescription>
            Hai già inserito dei dati per questo servizio. Se esci ora andranno persi.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="purchase-exit-cancel">Continua l'acquisto</AlertDialogCancel>
          <AlertDialogAction
            onClick={closeModal}
            className="bg-red-600 hover:bg-red-700"
            data-testid="purchase-exit-confirm-btn"
          >
            Esci e perdi i dati
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
